import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Plus as PlusIcon } from 'lucide-react';
import { VacationDay } from '../../types/schedule';
import { Card, Button, Select } from '../common';
import DatePicker from '../common/DatePicker';
import VacationDayList from './VacationDayList';
import { toast } from 'react-hot-toast';
import { useScheduleActions } from '../../hooks/useSchedule';
import { supabase } from '../../lib/supabase/client';
import { useScheduleStore } from '../../lib/store/scheduleStore';

// Define schema for vacation day input
const vacationDaySchema = z.object({
  providerId: z.string({ required_error: 'Provider is required' }),
  date: z.date({ required_error: 'Date is required' }),
});

type VacationDayFormValues = z.infer<typeof vacationDaySchema>;

interface VacationDayInputProps {
  scheduleId: string;
  vacationDays: VacationDay[];
  teamMembers: Array<{ user_id: string; full_name?: string }>;
  onVacationAdded?: () => void;
  onVacationRemoved?: () => void;
  isLoading?: boolean;
}

const VacationDayInput: React.FC<VacationDayInputProps> = ({
  scheduleId,
  vacationDays,
  teamMembers,
  onVacationAdded,
  onVacationRemoved,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const { addVacation, removeVacation } = useScheduleActions();
  const formSubmitAttemptRef = React.useRef(0);
  
  const { control, handleSubmit, formState: { errors }, reset, setValue } = useForm<VacationDayFormValues>({
    resolver: zodResolver(vacationDaySchema),
    defaultValues: {
      providerId: '',
      date: new Date(),
    }
  });
  
  const providerOptions = teamMembers?.map(member => {
    console.log('Mapping provider option:', member.user_id, member.full_name);
    return {
      value: member.user_id,
      label: member.full_name || member.user_id,
    };
  }) || [];
  
  // Get the fetchVacationDays function directly from the store
  const { fetchVacationDays } = useScheduleStore();
  
  // Add effect to get current user and set as default provider if available
  useEffect(() => {
    const setCurrentUserAsProvider = async () => {
      if (supabase) {
        try {
          const { data } = await supabase.auth.getUser();
          if (data?.user) {
            const currentUserId = data.user.id;
            console.log('Current user ID for default selection:', currentUserId);
            
            // Check if current user is in team members
            const isCurrentUserInTeam = teamMembers.some(member => member.user_id === currentUserId);
            
            if (isCurrentUserInTeam) {
              console.log('Setting current user as default provider');
              setSelectedProviderId(currentUserId);
              setValue('providerId', currentUserId);
            }
          }
        } catch (error) {
          console.error('Error getting current user:', error);
        }
      }
    };
    
    setCurrentUserAsProvider();
  }, [teamMembers, setValue]);
  
  useEffect(() => {
    if (teamMembers.length > 0) {
      console.log('Team members loaded in VacationDayInput:', teamMembers);
    }
  }, [teamMembers]);
  
  useEffect(() => {
    const fixEmptyProviderIds = async () => {
      if (vacationDays.length > 0 && teamMembers.length > 0) {
        // Look for vacation days with empty provider IDs
        const emptyProviderDays = vacationDays.filter(day => !day.providerId.trim());
        
        if (emptyProviderDays.length > 0) {
          console.log('Found vacation days with empty provider IDs:', emptyProviderDays);
          
          // Fix them by using the current user's ID (first team member)
          const defaultProvider = teamMembers[0];
          if (defaultProvider) {
            console.log('Using default provider for fix:', defaultProvider);
            
            // Remove the empty ones
            for (const day of emptyProviderDays) {
              await removeVacation(scheduleId, day);
            }
            
            // Add them back with the provider ID
            const fixedDays = emptyProviderDays.map(day => ({
              providerId: defaultProvider.user_id,
              date: day.date,
              providerName: defaultProvider.full_name
            }));
            
            await addVacation(scheduleId, fixedDays);
            console.log('Fixed vacation days with empty provider IDs');
            
            if (onVacationAdded) {
              onVacationAdded();
            }
          }
        }
      }
    };
    
    fixEmptyProviderIds();
  }, [vacationDays, teamMembers, scheduleId, removeVacation, addVacation, onVacationAdded]);
  
  useEffect(() => {
    // If there's exactly one team member, auto-select them
    if (teamMembers.length === 1 && !isSubmitting) {
      const defaultProvider = teamMembers[0];
      console.log('Auto-selecting the only provider:', defaultProvider);
      setSelectedProviderId(defaultProvider.user_id);
      reset({
        providerId: defaultProvider.user_id,
        date: new Date()
      });
    }
  }, [teamMembers, reset, isSubmitting]);
  
  const onSubmit = async (data: VacationDayFormValues) => {
    formSubmitAttemptRef.current += 1;
    console.log(`Form submission attempt #${formSubmitAttemptRef.current}`);
    console.log('Form data:', JSON.stringify(data, null, 2));
    setIsSubmitting(true);
    
    try {
      // Use the selectedProviderId as a fallback if the form value is empty
      const providerId = data.providerId || selectedProviderId;
      console.log('Selected provider ID:', providerId);
      console.log('Selected date:', data.date);
      console.log('All providers:', JSON.stringify(teamMembers, null, 2));
      
      if (!providerId) {
        console.error('No provider selected');
        toast.error('Please select a provider');
        setIsSubmitting(false);
        return;
      }
      
      // Debug the selected provider
      let selectedProvider;
      for (const member of teamMembers) {
        console.log(`Checking if member ${member.user_id} matches ${providerId}`);
        if (member.user_id === providerId) {
          selectedProvider = member;
          console.log('Match found!', JSON.stringify(member, null, 2));
          break;
        }
      }

      // Get the current user to check if we're adding a vacation day for ourselves
      if (!selectedProvider && supabase) {
        console.log('Provider not found in team members, checking if this is the current user');
        const { data: userData } = await supabase.auth.getUser();
        const currentUserId = userData?.user?.id;
        console.log('Current user ID:', currentUserId);
        console.log('Selected provider ID:', providerId);
        
        if (currentUserId === providerId) {
          console.log('Current user is adding vacation for themselves');
          // Allow the user to add vacation for themselves
          selectedProvider = {
            user_id: currentUserId,
            full_name: userData?.user?.user_metadata?.full_name || 'Current User'
          };
        }
      }

      if (!selectedProvider) {
        console.error('Provider not found in team members');
        toast.error('Selected provider not found in team members');
        setIsSubmitting(false);
        return;
      }
      
      const dateStr = format(data.date, 'yyyy-MM-dd');
      console.log('Formatted date:', dateStr);
      
      const vacationDay = {
        providerId: providerId,
        date: dateStr,
        providerName: selectedProvider.full_name
      };
      
      console.log('Creating vacation day with:', JSON.stringify(vacationDay, null, 2));
      
      // Use store action instead of fetch API
      await addVacation(scheduleId, [vacationDay]);
      
      console.log('Vacation day added successfully');
      toast.success('Vacation day added successfully');
      reset();
      
      // Manually refresh the vacation days data
      setTimeout(() => {
        try {
          console.log('Manually refreshing vacation days data');
          fetchVacationDays(scheduleId);
        } catch (refreshError) {
          console.error('Error refreshing vacation days:', refreshError);
        }
      }, 500);
    } catch (error) {
      console.error('Error adding vacation day:', error);
      toast.error('Failed to add vacation day: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleRemoveVacationDay = async (vacationDay: VacationDay) => {
    try {
      // Use store action instead of fetch API
      await removeVacation(scheduleId, vacationDay);
      
      toast.success('Vacation day removed successfully');
      
      // Call the onVacationRemoved callback if provided
      if (onVacationRemoved) {
        onVacationRemoved();
      }
      
      // Manually refresh the vacation days data
      setTimeout(() => {
        try {
          console.log('Manually refreshing vacation days data after removal');
          fetchVacationDays(scheduleId);
        } catch (refreshError) {
          console.error('Error refreshing vacation days:', refreshError);
        }
      }, 500);
    } catch (error) {
      console.error('Error removing vacation day:', error);
      toast.error('Failed to remove vacation day. Please try again.');
    }
  };
  
  // Handle direct provider selection change outside of React Hook Form
  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProviderId = e.target.value;
    console.log('Provider selected directly:', newProviderId);
    setSelectedProviderId(newProviderId);
    setValue('providerId', newProviderId);
  };
  
  return (
    <Card>
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Vacation Days</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add pre-approved vacation days that won't be scheduled.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Collapse' : 'Add Vacation Days'}
        </Button>
      </div>
      
      {isExpanded && (
        <div className="px-4 py-5 sm:p-6 border-t border-gray-200">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Controller
                  name="providerId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Provider"
                      value={selectedProviderId || field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        handleProviderChange(e);
                      }}
                      error={errors.providerId?.message}
                      disabled={isSubmitting || isLoading}
                      options={providerOptions}
                    />
                  )}
                />
              </div>
              
              <div>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Vacation Date"
                      selected={field.value}
                      onChange={field.onChange}
                      error={errors.date?.message}
                      disabled={isSubmitting || isLoading}
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<PlusIcon size={16} />}
                isLoading={isSubmitting}
                disabled={isLoading}
              >
                Add Vacation Day
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {vacationDays.length > 0 && (
        <div className="px-4 py-5 sm:p-6 border-t border-gray-200">
          <VacationDayList
            vacationDays={vacationDays}
            teamMembers={teamMembers}
            onRemove={handleRemoveVacationDay}
            isLoading={isLoading || isSubmitting}
          />
        </div>
      )}
    </Card>
  );
};

export default VacationDayInput; 